import { z } from "zod";

/** Поле формы, на которое ложится серверная ошибка. */
export type ServerErrorField = "fieldKey" | "name";

type ServerErrorRule = {
  field: ServerErrorField;
  /** Текст по умолчанию, если сервер не прислал своего. */
  message: string;
};

/**
 * Реестр по варианту кода ошибки — единственный источник правды.
 *
 * Уникальность имён и ключей полей проверяет backend в транзакции записи:
 * frontend видит только часть дерева и гарантировать её не может. Локальные
 * проверки остаются подсказкой, приоритет у серверного ответа.
 *
 * Добавление кода — одна строка здесь. Ни парсер, ни потребители не меняются.
 * Коды, которых тут нет, намеренно не разбираются: угадывать поле по тексту
 * сообщения нельзя, такой ответ показывается как обычная ошибка сохранения.
 */
export const serverErrorRegistry = {
  OBJECT_NAME_NOT_UNIQUE: {
    field: "name",
    message: "Объект с таким именем уже существует",
  },
  GROUP_NAME_NOT_UNIQUE: {
    field: "name",
    message: "Группа с таким именем уже существует",
  },
  ATTRIBUTE_NAME_NOT_UNIQUE: {
    field: "name",
    message: "Атрибут с таким именем уже существует",
  },
  ATTRIBUTE_FIELD_KEY_NOT_UNIQUE: {
    field: "fieldKey",
    message: "Такой ключ поля уже используется",
  },
} satisfies Record<string, ServerErrorRule>;

// Поиск по строковому коду из ответа сервера. Map, а не индексация литерала:
// иначе понадобилось бы приведение типа ключа.
const rules = new Map<string, ServerErrorRule>(
  Object.entries(serverErrorRegistry),
);

const zErrorItem = z.object({
  code: z.string(),
  message: z.string().nullish(),
});

/**
 * Конверт ошибки. Сервер отдаёт либо один код на верхнем уровне, либо список.
 * passthrough: остальные поля конверта нас не касаются и не должны ронять разбор.
 */
const zErrorBody = z
  .object({
    code: z.string().nullish(),
    message: z.string().nullish(),
    errors: zErrorItem.array().nullish(),
  })
  .passthrough();

const zErrorResponse = z.object({
  response: z
    .object({
      status: z.number().nullish(),
      data: zErrorBody.nullish(),
    })
    .nullish(),
});

/**
 * HTTP-статус ответа, если он есть.
 * Единственное место, которое знает форму объекта ошибки: и разбор кодов,
 * и классификация последствий опираются на него.
 */
export const httpStatusOf = (error: unknown): number | undefined => {
  const parsed = zErrorResponse.safeParse(error);
  return parsed.success
    ? (parsed.data.response?.status ?? undefined)
    : undefined;
};

export type FormFieldError = {
  name: ServerErrorField;
  errors: string[];
};

/**
 * Разбор ответа сервера в ошибки полей формы.
 *
 * Пустой список означает «известных кодов нет»: вызывающий код показывает
 * общую ошибку сохранения и не трогает введённые значения.
 */
export const parseServerFieldErrors = (error: unknown): FormFieldError[] => {
  const parsed = zErrorResponse.safeParse(error);
  const body = parsed.success ? parsed.data.response?.data : undefined;
  if (!body) return [];

  const items = [
    ...(body.code ? [{ code: body.code, message: body.message }] : []),
    ...(body.errors ?? []),
  ];
  const byField = new Map<ServerErrorField, string[]>();
  items.forEach(({ code, message }) => {
    const rule = rules.get(code);
    if (!rule) return;
    const messages = byField.get(rule.field) ?? [];
    messages.push(message || rule.message);
    byField.set(rule.field, messages);
  });
  return Array.from(byField, ([name, errors]) => ({ name, errors }));
};
